"use client";

import { useMemo, useState } from "react";
import { Button } from "@/components/UI/Button/Button";
import { CALENDAR_URL } from "@/utils/consts";
import cls from "./AppCostCalculator.module.css";

const platforms = [
    { id: "ios", label: "iOS only", multiplier: 1 },
    { id: "android", label: "Android only", multiplier: 1 },
    { id: "cross", label: "Cross-platform", multiplier: 1.4 },
    { id: "both", label: "Both native", multiplier: 1.9 },
    { id: "web", label: "Web app", multiplier: 0.8 },
] as const;

const complexities = [
    { id: "mvp", label: "MVP", cost: 2000, tier: 0 },
    { id: "beta", label: "Up to 10 beta users", cost: 45000, tier: 1 },
    { id: "production", label: "Production ready", cost: 120000, tier: 2 },
] as const;

const features = [
    { id: "payments", label: "Payments", costs: [1000, 3000, 6000] },
    { id: "ai", label: "AI", costs: [1000, 3000, 8000] },
    { id: "chat", label: "Chat", costs: [1000, 5000, 10000] },
    { id: "monitoring", label: "Monitoring", costs: [1000, 1000, 1000] },
    { id: "admin", label: "Admin panel", costs: [1000, 3000, 5000] },
    { id: "support", label: "Support bot", costs: [1000, 3000, 5000] },
] as const;

type PlatformId = (typeof platforms)[number]["id"];
type ComplexityId = (typeof complexities)[number]["id"];
type FeatureId = (typeof features)[number]["id"];

const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
});

const compactPrice = (value: number) => `+$${value / 1000}k`;

export const AppCostCalculator = () => {
    const [platform, setPlatform] = useState<PlatformId>("cross");
    const [complexity, setComplexity] = useState<ComplexityId>("mvp");
    const [selectedFeatures, setSelectedFeatures] = useState<FeatureId[]>([]);

    const estimate = useMemo(() => {
        const selectedPlatform = platforms.find((item) => item.id === platform) ?? platforms[2];
        const selectedComplexity = complexities.find((item) => item.id === complexity) ?? complexities[0];
        const featureCost = selectedFeatures.reduce((total, featureId) => {
            const feature = features.find((item) => item.id === featureId);
            return total + (feature?.costs[selectedComplexity.tier] ?? 0);
        }, 0);
        const expected = selectedComplexity.cost * selectedPlatform.multiplier + featureCost;

        return {
            optimistic: Math.round(expected * 0.86),
            expected: Math.round(expected),
            cautious: Math.round(expected * 1.14),
            platformLabel: selectedPlatform.label,
            complexityLabel: selectedComplexity.label,
        };
    }, [complexity, platform, selectedFeatures]);

    const toggleFeature = (featureId: FeatureId) => {
        setSelectedFeatures((current) =>
            current.includes(featureId)
                ? current.filter((id) => id !== featureId)
                : [...current, featureId],
        );
    };

    return (
        <section className={cls.calculator} aria-labelledby="app-cost-calculator-title">
            <div className={cls.heading}>
                <h3 id="app-cost-calculator-title" className={cls.title}>App cost estimator</h3>
            </div>

            <div className={cls.controls}>
                <fieldset className={cls.fieldset}>
                    <legend>Platform</legend>
                    <div className={cls.optionGrid}>
                        {platforms.map((item) => (
                            <label key={item.id} className={`${cls.option} ${platform === item.id ? cls.optionActive : ""}`}>
                                <input
                                    type="radio"
                                    name="app-platform"
                                    value={item.id}
                                    checked={platform === item.id}
                                    onChange={() => setPlatform(item.id)}
                                />
                                <span>{item.label}</span>
                            </label>
                        ))}
                    </div>
                </fieldset>

                <fieldset className={cls.fieldset}>
                    <legend>Complexity</legend>
                    <div className={cls.optionGrid}>
                        {complexities.map((item) => (
                            <label key={item.id} className={`${cls.option} ${complexity === item.id ? cls.optionActive : ""}`}>
                                <input
                                    type="radio"
                                    name="app-complexity"
                                    value={item.id}
                                    checked={complexity === item.id}
                                    onChange={() => setComplexity(item.id)}
                                />
                                <span>{item.label}</span>
                            </label>
                        ))}
                    </div>
                </fieldset>

                <fieldset className={cls.fieldset}>
                    <legend>Add features</legend>
                    <div className={cls.featureGrid}>
                        {features.map((item) => (
                            <label key={item.id} className={`${cls.option} ${selectedFeatures.includes(item.id) ? cls.optionActive : ""}`}>
                                <input
                                    type="checkbox"
                                    checked={selectedFeatures.includes(item.id)}
                                    onChange={() => toggleFeature(item.id)}
                                />
                                <span>{item.label}</span>
                                <span className={cls.price}>{compactPrice(item.costs[complexities.find((entry) => entry.id === complexity)?.tier ?? 0])}</span>
                            </label>
                        ))}
                    </div>
                </fieldset>
            </div>

            <div className={cls.result} aria-live="polite">
                <div className={cls.resultHead}>
                    <span>Estimated build</span>
                    <strong>{currency.format(estimate.optimistic)}–{currency.format(estimate.cautious)}</strong>
                </div>
                <p className={cls.selection}>{estimate.platformLabel} · {estimate.complexityLabel}</p>
                <div className={cls.scenario}>
                    <span>Optimistic</span>
                    <strong>{currency.format(estimate.optimistic)}</strong>
                </div>
                <div className={cls.scenario}>
                    <span>Expected</span>
                    <strong>{currency.format(estimate.expected)}</strong>
                </div>
                <div className={cls.scenario}>
                    <span>Cautious</span>
                    <strong>{currency.format(estimate.cautious)}</strong>
                </div>
                <Button className={cls.cta} href={CALENDAR_URL}>
                    Pin this down in 48h →
                </Button>
                <p className={cls.footnote}>Planning ranges, not a quote. Assumes an outsourced team; excludes freelancer-marketplace rates.</p>
            </div>
        </section>
    );
};
